import { Dispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../Store/store";
import { connect, useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { setTogglePropertyClaimedModal } from "../../../Redux/propertyReducer";
import {
  useLazyGetUserPropertyListQuery,
  usePostClaimedPropertyRequestMutation,
} from "../../../Services/api.user.slice";
import toast from "react-hot-toast";
import LoaderOverlay from "../../Loader/Loader";
import { CLAIM } from "../../../Configurations/SiteCopy";

interface PropertyClaimedModalProps {
  propertyReducer: {
    togglePropertyClaimedModal?: boolean;
    /** Someone already owns it: this is a dispute / proof of ownership. */
    claimModalAlreadyClaimed?: boolean;
  };
}

const PropertyClaimedModal = (props: PropertyClaimedModalProps) => {
  const dispatch = useDispatch();
  const [isClaimed, setIsClaimed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rightsAgreed, setRightsAgreed] = useState(false);

  const [claimedProperty, { isLoading: apnFileUploadLoading }] =
    usePostClaimedPropertyRequestMutation();
  const [fetchPropertyList] = useLazyGetUserPropertyListQuery();

  const { propertyReducer } = props;
  const { togglePropertyClaimedModal } = propertyReducer;
  const alreadyClaimed = Boolean(propertyReducer.claimModalAlreadyClaimed);

  // Reset state when modal opens for a new property
  useEffect(() => {
    if (togglePropertyClaimedModal) {
      setIsClaimed(false);
      setFile(null);
      setRightsAgreed(false);
    }
  }, [togglePropertyClaimedModal]);

  const canSubmit = Boolean(file) && rightsAgreed;

  const handleClaimProperty = async () => {
    setIsClaimed(false);
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (!rightsAgreed) {
      toast.error("Please agree to the ownership declaration to continue.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const { togglePropertyClaimedModal } = propertyReducer;
    formData.append("propertyId", String(togglePropertyClaimedModal || "")); // Replace with actual property ID
    formData.append("rightsAgreed", "true");

    try {
      const result = await claimedProperty(formData);

      // Check for RTK Query error response
      if ('error' in result) {
        const errorData = result.error as any;
        console.error("APN Upload API Error:", errorData);
        const errorMessage = errorData?.data?.errors?.message ||
                            errorData?.data?.message ||
                            "The process has failed, kindly report it to admin or info@simplerhome.app";
        toast.error(errorMessage, { duration: 5000 });
        return;
      }

      const { data } = result;
      fetchPropertyList("");

      // Check for API error in response
      if (data?.isApiError) {
        console.error("APN Upload Response Error:", data);
        toast.error(data?.errors?.message || "The process has failed, kindly report it to admin or info@simplerhome.app", { duration: 5000 });
        return;
      }

      // Success
      setIsClaimed(true);
      setFile(null);
      toast.success(
        alreadyClaimed
          ? "Thanks. Our team will review your documents and email you."
          : "Your document has been uploaded and will get reviewed soon.",
        { duration: 5000 }
      );
      dispatch(setTogglePropertyClaimedModal(false));
      // No page refresh - just close the modal
    } catch (error: any) {
      console.error("Claim property error:", error);
      toast.error("The process has failed, kindly report it to admin or info@simplerhome.app", { duration: 5000 });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  return (
    <Container maxWidth="sm">
      {apnFileUploadLoading && <LoaderOverlay />}
      <Modal
        open={Boolean(togglePropertyClaimedModal)}
        onClose={() => {
          dispatch(setTogglePropertyClaimedModal(false));
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={() => dispatch(setTogglePropertyClaimedModal(false))}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Card elevation={0}>
            <CardContent>
              <Typography variant="h5" gutterBottom textAlign={"center"}>
                {alreadyClaimed ? "This home has already been claimed" : "Claim Your Property"}
              </Typography>
              {/* Oct 20 call: say plainly that it's taken, and how to dispute it. */}
              {alreadyClaimed && !isClaimed && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="claim-already-claimed">
                  Someone has already verified this home on SimplerHome. If you think that's a mistake, or you've
                  bought it, send us proof of ownership: a deed, closing statement or property tax bill. Our team
                  reviews it, and if it checks out the home moves to your account.
                </Alert>
              )}
              {isClaimed ? (
                <Typography color="success.main" textAlign={"center"}>
                  Claiming Property... In Progress
                </Typography>
              ) : (
                <>
                  {/* <TextField
                    // label="Enter Claim Code"
                    // variant="outlined"
                    type="file"
                    fullWidth
                    value={claimCode}
                    onChange={(e) => setClaimCode(e.target.value)}
                    margin="normal"
                  /> */}
                  <Typography fontSize={"0.75rem"} mt={alreadyClaimed ? 2.5 : 4}>
                    {alreadyClaimed
                      ? "Proof of ownership (image or PDF)"
                      : "Upload Property Document Containing APN No. (Image or PDF)"}
                  </Typography>
                  <TextField
                    sx={{ mt: 1 }}
                    type="file"
                    fullWidth
                    onChange={handleFileChange}
                    margin="normal"
                    inputProps={{ accept: "image/*,.pdf,application/pdf" }}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      mb: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "grey.300",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        color: "text.primary",
                        lineHeight: 1.5,
                        mb: 1,
                      }}
                      data-testid="claim-rights-text"
                    >
                      {CLAIM.RIGHTS_AGREEMENT_LABEL}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rightsAgreed}
                          onChange={(e) => setRightsAgreed(e.target.checked)}
                          inputProps={{
                            "aria-label": "Agree to ownership declaration",
                          }}
                        />
                      }
                      label={CLAIM.RIGHTS_AGREEMENT_CHECKBOX_HINT}
                      slotProps={{
                        typography: { fontSize: "0.875rem" },
                      }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={handleClaimProperty}
                    disabled={!canSubmit}
                    sx={{ color: 'white' }}
                  >
                    {alreadyClaimed ? "Send proof of ownership" : "Claim Property"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </Container>
  );
};

function mapStateToProps(state: RootState) {
  return {
    propertyReducer: state.propertyReducer,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {};
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PropertyClaimedModal);
